<template>
  <div class="comment-list">
    <div class="title-wrapper">
      <span class="arrow" @click="back">＜</span>
      <span class="title">{{ contentTitle }}</span>
    </div>
    <div ref="list" class="list">
      <transition-group name="tran" v-if="commentList.length">
        <div v-for="item in commentList" :key="item.commentId" class="item" :class="{ own: isOwnComment(item.userId) }">
          <textarea
            v-if="isEditing(item.commentId)"
            class="textarea"
            :rows="rows(editTarget.comment)"
            v-model="editTarget.comment"
          />
          <vue-markdown v-else :source="item.comment" :html="false" class="comment" />
          <div class="sub">
            <div v-if="isEditing(item.commentId)" class="edit-mode">
              <div class="btn delete" @click="onClickDelete(item)">削除する</div>
              <div class="btn save" @click="updateComment">保存する</div>
            </div>
            <div v-else class="normal-mode">
              <div class="post-data">
                <div class="created-at">{{ createdAt(item.createdAt) }}</div>
                <div class="user-name">{{ item.name || "名無し" }}</div>
              </div>
              <div v-if="isOwnComment(item.userId)" class="btn edit" @click="edit(item)">編集する</div>
              <div v-else class="btn reply" @click="reply(item.comment)">返信する</div>
            </div>
          </div>
        </div>
      </transition-group>
      <div v-else class="description">
        <div>コメントはありません</div>
      </div>
    </div>
    <div class="comment-post">
      <textarea ref="textarea" class="textarea" :rows="rows(comment)" v-model="comment" />
      <button class="button submit" @click="post" :disabled="!comment">送信</button>
    </div>
    <div v-if="isDeleting" class="delete-mask">
      <h2>このコメントを削除しますか？</h2>
      <div>一度削除したコメントは復旧できません。</div>
      <div class="delete-controller">
        <button class="button delete" @click="deleteComment">削除する</button>
        <button class="button cancel" @click="cancelDelete">キャンセル</button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { Manuscript, Comment, User, SharedLinkKind, SubPlot } from "@/lib/models";
import { Comment as CommentDexie } from "@/lib/indexeddb";
import VueMarkdown from "vue-markdown";
import clone from "lodash/cloneDeep";
import { format } from "date-fns";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    VueMarkdown,
  },
  props: {
    novelId: {
      type: String as PropType<string>,
    },
    sharedLinkId: {
      type: String as PropType<string>,
    },
    contentKey: {
      type: String as PropType<string>,
    },
    kind: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      comment: "",
      editTarget: null,
      deleteTarget: null,
      isDeleting: false,
    };
  },
  methods: {
    post() {
      const { sharedLinkId, contentKey, novelId, comment } = this;
      const { name } = this.user;

      this.$store.dispatch("sharedLinkModule/createCommentBelongOwner", {
        sharedLinkId,
        manuscriptKey: this.kind === SharedLinkKind.MANUSCRIPT ? contentKey : undefined,
        plotId: this.kind === SharedLinkKind.PLOT ? contentKey : undefined,
        novelId,
        name: name || "",
        comment,
        callback: async () => {
          this.comment = "";
          await this.$nextTick();
          this.scrollToBottom();
        },
      });
    },
    updateComment() {
      const { sharedLinkId, commentId, manuscriptKey, plotId, novelId, name, comment } = this.editTarget as Comment;

      this.$store.dispatch("sharedLinkModule/updateCommentBelongOwner", {
        sharedLinkId,
        commentId,
        manuscriptKey,
        plotId,
        novelId,
        name,
        comment,
        callback: () => {
          this.initializeEditFragment();
        },
      });
    },
    onClickDelete(comment: Comment) {
      this.isDeleting = true;
      this.deleteTarget = comment;
    },
    cancelDelete() {
      this.initializeDeleteFragment();
    },
    deleteComment() {
      const { sharedLinkId, commentId } = this.deleteTarget as Comment;
      this.$store.dispatch("sharedLinkModule/deleteCommentBelongOwner", {
        sharedLinkId,
        commentId,
        callback: () => {
          this.initializeEditFragment();
          this.initializeDeleteFragment();
        },
      });
    },
    initializeEditFragment() {
      this.editTarget = null;
    },
    initializeDeleteFragment() {
      this.isDeleting = false;
      this.deleteTarget = null;
    },
    async reply(comment: string) {
      // eslint-disable-next-line vue/no-parsing-error
      /** 文字の先頭に引用符を付ける */
      const splittedComment = comment.split("\n");
      const addedQuoteComment = splittedComment.map((comment) => `> ${comment}`);
      this.comment = `${addedQuoteComment.join("\n")}\n\n`;

      /** this.commentが反映されるまで待つ */
      await this.$nextTick();

      /** textareaにフォーカスする */
      const textareaElement = this.$refs.textarea;
      textareaElement.focus();
      textareaElement.scrollTop = textareaElement.scrollHeight;
    },
    edit(commentObj: Comment) {
      this.editTarget = clone(commentObj);
    },
    scrollToBottom() {
      const listElement = this.$refs.list;
      listElement.scrollTop = listElement.scrollHeight;
    },
    back() {
      this.$store.dispatch("sharedLinkModule/initializeSelectedContent");
    },
  },
  computed: {
    selectedSharedLinkId() {
      return this.$store.getters["sharedLinkModule/selectedSharedLinkId"];
    },
    selectedContentKey() {
      return this.$store.getters["sharedLinkModule/selectedContentKey"];
    },
    commentList() {
      const commentList = this.$store.getters["sharedLinkModule/commentListBelongOwner"](
        this.selectedSharedLinkId
      ) as Comment[];

      switch (this.kind) {
        case SharedLinkKind.MANUSCRIPT:
          return commentList.filter((comment) => comment.manuscriptKey === this.contentKey);
        case SharedLinkKind.PLOT:
          return commentList.filter((comment) => comment.plotId === this.contentKey);
        default:
          return [];
      }
    },
    contentTitle() {
      switch (this.kind) {
        case SharedLinkKind.MANUSCRIPT: {
          const manuscriptList = this.$store.getters["manuscriptModule/manuscriptList"] as Manuscript[];
          const manuscript: Manuscript = manuscriptList.find(
            (manuscript) => manuscript.key === this.selectedContentKey
          )!;
          return manuscript.title ? manuscript.title : "（タイトル未設定）";
        }
        case SharedLinkKind.PLOT: {
          const overallPlotIdPrefix = "overall-plot-";
          if (this.selectedContentKey.startsWith(overallPlotIdPrefix)) {
            return "全体プロット";
          }
          const subPlots = this.$store.getters["plotModule/subPlots"];
          const subPlot: SubPlot = subPlots.find((subPlot: SubPlot) => subPlot.plotId === this.selectedContentKey)!;
          if (subPlot) {
            return subPlot.name ? subPlot.name : "（タイトル未設定）";
          }
          return "（タイトル未設定）";
        }
        default:
          return "";
      }
    },
    user() {
      return this.$store.getters["userModule/user"];
    },
    rows() {
      return (comment: string) => {
        const breakNum = comment.split("\n").length;
        return breakNum < 10 ? breakNum : 10;
      };
    },
    createdAt() {
      return (date: string) => format(date, "YYYY/MM/DD HH:mm");
    },
    isEditing() {
      return (commentId: string) => this.editTarget && this.editTarget.commentId === commentId;
    },
    isOwnComment() {
      return (userId: string) => userId === this.user.userId;
    },
  },
  async created() {
    const { novelId, sharedLinkId, contentKey, kind } = this;
    const { userId } = this.user as User;
    const unreadCommentList = this.$store.getters["sharedLinkModule/unreadCommentList"](
      userId,
      novelId,
      kind,
      sharedLinkId,
      contentKey
    ) as CommentDexie[];
    if (!unreadCommentList.length) return;

    /** 未読コメントを既読にする */
    this.$store.dispatch("sharedLinkModule/readComment", unreadCommentList);
  },
  mounted() {
    this.scrollToBottom();
  },
});

interface Props {
  novelId: string;
  sharedLinkId: string;
  contentKey: string;
  kind: string;
}

interface Data {
  comment: string;
  editTarget: Comment | null;
  deleteTarget: Comment | null;
  isDeleting: boolean;
}

interface Methods {
  post: () => void;
  onClickDelete: (comment: Comment) => void;
  cancelDelete: () => void;
  initializeDeleteFragment: () => void;
  initializeEditFragment: () => void;
  deleteComment: (comment: Comment) => void;
  updateComment: () => void;
  reply: (comment: string) => void;
  edit: (commentObj: Comment) => void;
  scrollToBottom: () => void;
  back: () => void;
}

interface Computed {
  selectedSharedLinkId: string;
  selectedContentKey: string;
  commentList: Comment[];
  contentTitle: string;
  user: User;
  rows: (comment: string) => number;
  createdAt: (date: string) => string;
  isEditing: (commentId: string) => boolean;
  isOwnComment: (userId: string) => boolean;
}
</script>

<style lang="scss" scoped>
@import "@/style-common.scss";

@mixin link {
  background: transparent;
  border: none;
  cursor: pointer;
}

.comment-list {
  width: 100%;
  border: 1px solid var(--color-border);
  border-top-left-radius: 10px;
  background-color: var(--color-background-normal);

  height: 100%;
  display: flex;
  flex-direction: column;

  .title-wrapper {
    display: flex;
    align-items: center;
    font-size: var(--font-size-md);
    font-weight: var(--font-weight-bold);
    margin: 12px 0;
    margin-left: 16px;
    margin-right: 4px;
    color: var(--color-text-normal);

    .arrow {
      margin-right: 10px;
      cursor: pointer;

      &:hover {
        color: var(--color-primary);
      }
    }

    .title {
      user-select: none;
    }
  }
}

.list {
  flex-grow: 1;
  overflow-y: auto;

  .item {
    position: relative;
    box-sizing: border-box;
    border-bottom: 1px solid var(--color-border);
    padding: 10px 15px;
    color: var(--color-text-normal);
    @include hover-overlay;

    &.own {
      background-color: var(--color-surface-subtle);

      [data-app-theme="dark"] & {
        background-color: rgba(79, 140, 201, 0.15);
      }
    }

    &.active {
      background-color: var(--color-surface-subtle);
    }

    .textarea {
      width: 230px;
      margin: 10px 0;
      padding: 10px;
      resize: none;
      font-size: 1.1em;
      border-radius: var(--radius-xs);
      background-color: var(--color-background-normal);
      color: var(--color-text-normal);
      border: 1px solid var(--color-border);
    }

    .comment {
      font-size: 1.1em;
    }

    .sub {
      .normal-mode {
        display: flex;
        justify-content: space-between;
        align-items: center;
        color: var(--color-text-low);

        .post-data {
          display: flex;
          align-items: center;
          width: 200px;

          .created-at {
            margin-right: 10px;
          }
        }
      }

      .edit-mode {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .btn {
        font-weight: var(--font-weight-bold);
        cursor: pointer;

        &.reply,
        &.save {
          color: var(--color-primary);
        }

        &.delete {
          color: var(--color-warning);
        }

        &.edit {
          color: var(--color-text-low);
        }
      }
    }
  }

  .description {
    margin: 20px 15px;
    padding: 20px 10px;
    color: var(--color-text-low);
  }
}

.comment-post {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  box-sizing: border-box;
  padding: 15px;
  background-color: var(--color-bg-dark);

  .textarea {
    flex-grow: 1;
    padding: 10px;
    resize: none;
    font-size: 1.1em;
    border-radius: var(--radius-xs);
    background-color: var(--color-background-normal);
    color: var(--color-text-normal);
    border: 1px solid var(--color-border);
  }

  .submit {
    padding: 0;
    margin: 0;
    margin-left: 15px;
    margin-bottom: 3px;
    border: none;
    color: var(--color-text-on-fixed-bg);
    background-color: transparent;
    font-weight: var(--font-weight-bold);
    cursor: pointer;
    font-size: 1.25em;

    &:disabled {
      cursor: not-allowed;
      color: var(--color-text-low);
    }
  }
}

.delete-mask {
  position: absolute;
  width: calc(100% - 36px);
  height: 100%;
  color: var(--color-text-on-fixed-bg);
  background-color: var(--color-overlay);
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  z-index: 1;
  box-sizing: border-box;
  padding: 20px;

  .delete-controller {
    margin-top: 50px;

    .button {
      border: solid 1px var(--color-text-on-fixed-bg);
      border-radius: var(--radius-xs);
      width: 100px;
      cursor: pointer;
    }

    .delete {
      color: var(--color-text-on-fixed-bg);
      background-color: var(--color-warning);
    }
    .cancel {
      background-color: transparent;
      color: var(--color-text-on-fixed-bg);
    }
  }
}
</style>

<style lang="scss">
/** vue-markdown用スタイル */

blockquote {
  padding: 5px 10px;
  margin: 0 0 15px;
  border-left: 5px solid var(--color-border);
}

code {
  padding: 2px 4px;
  font-size: 90%;
  color: #c7254e;
  background-color: #f9f2f4;
  border-radius: var(--radius-xs);

  [data-app-theme="dark"] & {
    color: #f78ca2;
    background-color: rgba(247, 140, 162, 0.15);
  }
}

pre {
  display: block;
  padding: 9.5px;
  margin: 0 0 10px;
  font-size: var(--font-size-xs);
  line-height: 1.42857143;
  color: var(--color-text-normal);
  word-break: break-all;
  word-wrap: break-word;
  background-color: var(--color-surface-subtle);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-xs);

  code {
    padding: 0;
    font-size: inherit;
    color: inherit;
    white-space: pre-wrap;
    background-color: transparent;
    border-radius: 0;
  }
}

.table {
  width: 100%;
  max-width: 100%;
  margin-bottom: 20px;
  background-color: transparent;
  border-spacing: 0;
  border-collapse: collapse;

  th {
    vertical-align: bottom;
    border-bottom: 2px solid var(--color-border);
    padding: 8px;
  }

  td {
    padding: 8px;
    vertical-align: top;
    border-top: 1px solid var(--color-border);
  }
}

dl {
  margin-top: 0;
  margin-bottom: 20px;
}

dt {
  font-weight: var(--font-weight-bold);
}

dd {
  margin-left: 0;
}
</style>
